/* eslint-disable max-lines */
import { gql } from '@apollo/client';
import { userProductPriceFragment } from '../../pricing/queries/useProductPrices';

const ProductItemFragment = gql`
    fragment RelatedProductDetailsFragment on Product {
        key
        groupId
        media {
            type
            url
            renditionUrl
        }
        customerPrice(
            cacheKey: $customerPriceCacheKey
            cartType: $cartType
            cartId: $cartId
        ) @include(if: $includeRelatedItemsPrice) {
            ...UserProductPriceFragment
        }
        manufacturer
        sku
        id
        name
        rebateCode @include(if: $isSalesRepresentative)
        groupId
        additionalCharges {
            chargeIncl
            chargeExcl
        }
        preorder
    }
`;

const ProductItemFragmentWithVariants = gql`
    fragment RelatedProductWithVariantsDetailsFragment on Product {
        ...RelatedProductDetailsFragment
        variants {
            id
            name
            sku
            size
            groupId
            rebateCode @include(if: $isSalesRepresentative)
            variationAttributes {
                name
                value
            }
            key
            additionalCharges {
                chargeIncl
                chargeExcl
            }
            parent {
                id
                key
                rebateCode @include(if: $isSalesRepresentative)
                groupId
            }
            customerPrice(
                cacheKey: $customerPriceCacheKey
                cartType: $cartType
            ) @include(if: $includeRelatedItemsPrice) {
                ...UserProductPriceFragment
            }
        }
    }
`;

const ProductVariantItemFragment = gql`
    fragment RelatedVariantProductDetailsFragment on ProductVariant {
        key
        variantSku: sku
        id
        name
        rebateCode @include(if: $isSalesRepresentative)
        groupId
        variationAttributes {
            name
            value
        }
        parent {
            ...RelatedProductDetailsFragment
        }
        additionalCharges {
            chargeIncl
            chargeExcl
        }
        customerPrice(
            cacheKey: $customerPriceCacheKey
            cartType: $cartType
            cartId: $cartId
        ) @include(if: $includeRelatedItemsPrice) {
            ...UserProductPriceFragment
        }
    }
`;

const productDetailsQuery = gql`
    query productDetailsLoggedIn(
        $store: ID!
        $language: Language!
        $id: ID
        $sku: String
        $isLoggedIn: Boolean!
        $customerPriceCacheKey: ID
        $cartType: CartType
        $cartId: Int
        $includeRelatedItemsPrice: Boolean!
        $includePrice: Boolean!
        $preorderSeasonId: Int
        $includePreorderSeasonId: Int
        $isSalesRepresentative: Boolean!
    ) {
        product(
            store: $store
            language: $language
            id: $id
            sku: $sku
            preorderSeasonId: $preorderSeasonId
            includePreorderSeasonId: $includePreorderSeasonId
        ) {
            id
            key
            name
            sku
            summary
            featuredAttribute
            isNew: new
            onSale
            manufacturer
            manufacturerLogo
            description
            rebateCode @include(if: $isSalesRepresentative)
            groupId
            preorder
            media {
                type
                url
                renditionUrl
            }
            customerPrice(
                cacheKey: $customerPriceCacheKey
                cartType: $cartType
                cartId: $cartId
            ) @include(if: $includePrice) {
                ...UserProductPriceFragment
            }
            pictograms {
                url
                label
                renditionUrl
                information
            }
            videos {
                embedUrl
            }
            siblingAttributes {
                name
                value
            }
            updated
            created
            additionalCharges {
                chargeIncl
                chargeExcl
            }
            variants {
                id
                key
                sku
                name
                description
                isNew: new
                groupId
                rebateCode @include(if: $isSalesRepresentative)
                variationAttributes {
                    name
                    value
                }
                specificationAttributes {
                    name
                    value
                }
                additionalCharges {
                    chargeIncl
                    chargeExcl
                }
                parent {
                    id
                    key
                    groupId
                    rebateCode @include(if: $isSalesRepresentative)
                }
                customerPrice(
                    cacheKey: $customerPriceCacheKey
                    cartType: $cartType
                    cartId: $cartId
                ) @include(if: $includePrice) {
                    ...UserProductPriceFragment
                }
            }
            categories {
                id
                name
                key
            }
            tileAttributes {
                name
                value
            }
            specificationAttributes {
                name
                value
            }
            siblings {
                id
                key
                sku
                siblingAttributes {
                    name
                    value
                }
                rebateCode @include(if: $isSalesRepresentative)
                groupId
                additionalCharges {
                    chargeIncl
                    chargeExcl
                }
                media {
                    renditionUrl
                }
                variants {
                    id
                    key
                    sku
                    name
                    groupId
                    rebateCode @include(if: $isSalesRepresentative)
                    variationAttributes {
                        name
                        value
                    }
                    parent {
                        id
                        key
                        rebateCode @include(if: $isSalesRepresentative)
                        groupId
                    }
                    additionalCharges {
                        chargeIncl
                        chargeExcl
                    }
                }
                preorder
            }
            vehicles {
                id
                manufacturerName
                model
                ccm
                ratedPower
                modelAddition
                typeNumbers
                typeAcceptanceList
                manufacturingStartYear
                manufacturingEndYear
            }
            accessories {
                ... on Product {
                    ...RelatedProductWithVariantsDetailsFragment
                }
                ... on ProductVariant {
                    ...RelatedVariantProductDetailsFragment
                }
            }
            spareParts {
                ... on Product {
                    ...RelatedProductWithVariantsDetailsFragment
                }
                ... on ProductVariant {
                    ...RelatedVariantProductDetailsFragment
                }
            }
            relatedProducts {
                ... on Product {
                    ...RelatedProductWithVariantsDetailsFragment
                }
                ... on ProductVariant {
                    ...RelatedVariantProductDetailsFragment
                }
            }
            alternativeProducts {
                ... on Product {
                    ...RelatedProductWithVariantsDetailsFragment
                }
                ... on ProductVariant {
                    ...RelatedVariantProductDetailsFragment
                }
            }
            replacementProducts {
                ... on Product {
                    ...RelatedProductWithVariantsDetailsFragment
                }
                ... on ProductVariant {
                    ...RelatedVariantProductDetailsFragment
                }
            }
            documents {
                url
                name
            }
            additionalCharges {
                chargeIncl
                chargeExcl
            }
            hazardStatements
            safetyDatasheetUrl
        }
    }
    ${ProductItemFragment}
    ${ProductVariantItemFragment}
    ${ProductItemFragmentWithVariants}
    ${userProductPriceFragment}
`;

export default productDetailsQuery;
